import type { ReactNode } from "react";
import type React from "react";
import { useEffect, useMemo, useState } from "react";

import dayjs from "dayjs";

import { PixelPlaceholderLoader } from "@similarweb/ui-components";

import { getCountries } from "app/components/filters-bar/utils";
import { AssetsContainer } from "app/pages/digital-suite/home-page/components/CustomAssets/Containers/AssetsContainer/AssetsContainer";
import {
    getDashboards,
    getKeywordLists,
    getLastViewedAssets,
} from "app/pages/digital-suite/home-page/components/CustomAssets/customAssetsApi";
import {
    navigateToDashboard,
    navigateToKeywordList,
    getUserWebsite,
} from "app/pages/digital-suite/home-page/components/CustomAssets/customAssetsUtils";
import { DashboardsEmptyState } from "app/pages/digital-suite/home-page/components/CustomAssets/EmptyStates/DashboardsEmptyState";
import { KeywordListHoverFooter } from "app/pages/digital-suite/home-page/components/CustomAssets/KeywordListHoverFooter/KeywordListHoverFooter";
import {
    WidgetContainer,
    ContainerLabel,
    TableWrapper,
    WidgetHeader,
    WidgetTitle,
} from "app/pages/digital-suite/home-page/components/CustomAssets/styledComponents";
import { AssetName, AssetType } from "app/pages/digital-suite/home-page/components/CustomAssets/types";
import type {
    IDashboardAsset,
    AssetProps,
    AssetItem,
    ILastViewedItem,
    IKeywordListItem,
    IKeywordListAsset,
} from "app/pages/digital-suite/home-page/components/CustomAssets/types";
import { WidgetWrapper } from "app/pages/digital-suite/home-page/components/HomePageTrialNTLayout/DigitalSuiteTrialHomePageStyled";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import I18n from "pro-features/components/WithTranslation/src/I18n";

import { KeywordListEmptyState } from "./EmptyStates/KeywordListEmptyState";

const assetFunctionMap = {
    [AssetName.Dashboard]: getDashboards,
    [AssetName.KeywordLists]: getKeywordLists,
};
type AssetsPromise = (AssetItem | ILastViewedItem)[] | void;
type AssetPromiseResult = [IDashboardAsset[] | void, IKeywordListItem[] | void, ILastViewedItem[]];

interface ICustomAssetsWidgetProps {
    title: string;
    ctaButton?: ReactNode;
    /**
     * the types of assets that will be shown in the widget
     */
    assetTypes?: AssetName[];
    /**
     * the types of assets that will be shown in the empty state
     */
    emptyStateTypes?: AssetName[];
}

export const CustomAssetsWidget: React.FC<ICustomAssetsWidgetProps> = ({
    title,
    ctaButton,
    assetTypes = Object.keys(assetFunctionMap),
    emptyStateTypes = Object.keys(assetFunctionMap),
}) => {
    const [assets, setAssets] = useState<AssetProps[]>([]);
    const [loadingAssets, setLoadingAssets] = useState<boolean>(true);
    const [dashboards, setDashboards] = useState<IDashboardAsset[]>([]);
    const [keywordLists, setKeywordLists] = useState<IKeywordListAsset[]>([]);

    useEffect(() => {
        const assetsPromises: Promise<AssetsPromise>[] = Object.entries(assetFunctionMap).map(
            ([assetName, assetFunction]) => {
                if (assetTypes.includes(assetName as AssetName)) {
                    return assetFunction();
                }
                return Promise.resolve();
            },
        );

        assetsPromises.push(getLastViewedAssets());

        Promise.all(assetsPromises)
            .then(([dashboards, keywordLists, lastViewedAssets]: AssetPromiseResult) => {
                const mappedDashboards = (dashboards || []).map((dashboard) => ({
                    ...dashboard,
                    lastSeen: getLastSeenByAssetId(dashboard, AssetType.DASHBOARD, lastViewedAssets),
                    type: AssetName.Dashboard,
                    onClick: () => {
                        navigateToDashboard(dashboard.id);
                    },
                }));

                const mappedKeywordLists = (keywordLists || []).map((keywordList) => ({
                    ...keywordList,
                    lastSeen: getLastSeenByAssetId(keywordList, AssetType.KEYWORD_LIST, lastViewedAssets),
                    type: AssetName.KeywordLists,
                    onClick: () => {
                        navigateToKeywordList(keywordList.id);
                    },
                    hoverFooter: (
                        <KeywordListHoverFooter
                            keywords={keywordList.keywords}
                            countryCode={getCountries()[0]?.id ?? WORLDWIDE_COUNTRY_ID}
                        />
                    ),
                }));

                setDashboards(mappedDashboards);
                setKeywordLists(mappedKeywordLists);
                sortAssets([...mappedDashboards, ...mappedKeywordLists]);
            })
            .finally(() => setLoadingAssets(false));
    }, []);

    const sortAssets = (assets: AssetProps[]): void => {
        setAssets(
            assets.sort((a: AssetProps, b: AssetProps) => {
                const aDate = dayjs(a.lastSeen ?? a.addedTime);
                const bDate = dayjs(b.lastSeen ?? b.addedTime);
                return aDate.isAfter(bDate) ? -1 : 1;
            }),
        );
    };

    const getLastSeenByAssetId = (
        asset: AssetItem,
        assetType: AssetType,
        lastViewedAssets: ILastViewedItem[],
    ): string | undefined => {
        const lastViewedAsset = lastViewedAssets.find(
            (lastViewedAsset) => lastViewedAsset.assetId === asset.id && lastViewedAsset.assetType === assetType,
        );

        if (lastViewedAsset?.lastViewed) {
            return lastViewedAsset.lastViewed;
        }
        if (!("owner" in asset)) {
            return asset.addedTime;
        }
        return undefined;
    };

    const shouldShowDashboardEmptyState = !dashboards.length && emptyStateTypes.includes(AssetName.Dashboard);
    const shouldShowKeywordListEmptyState = !keywordLists.length && emptyStateTypes.includes(AssetName.KeywordLists);

    const hasEmptyStates = useMemo<boolean>(() => {
        return shouldShowDashboardEmptyState || shouldShowKeywordListEmptyState;
    }, [shouldShowDashboardEmptyState, shouldShowKeywordListEmptyState]);

    return (
        <WidgetWrapper data-automation="platform-homepage-custom-assets">
            <WidgetContainer>
                <WidgetHeader
                    data-automation={"my-assets-header"}
                    isSubTitleAdjacent={!assets.length ? shouldShowDashboardEmptyState : null}
                >
                    <WidgetTitle data-automation={"my-assets-title"}>
                        <I18n>{title}</I18n>
                    </WidgetTitle>
                    {!loadingAssets ? ctaButton : null}
                </WidgetHeader>
                {loadingAssets ? (
                    <TableWrapper>
                        <PixelPlaceholderLoader
                            width={"100%"}
                            height={275}
                        />
                    </TableWrapper>
                ) : (
                    <>
                        {assets.length ? (
                            <TableWrapper marginBottom={hasEmptyStates ? 32 : 0}>
                                <AssetsContainer
                                    columns={3}
                                    maxRows={4}
                                    assets={assets}
                                />
                            </TableWrapper>
                        ) : null}
                        {shouldShowDashboardEmptyState ? (
                            <ContainerLabel isTitleAdjacent={!assets.length}>
                                <I18n>digitalmarketing.homepage.custom_assets.empty_state.title</I18n>
                            </ContainerLabel>
                        ) : null}
                        {shouldShowKeywordListEmptyState ? (
                            <KeywordListEmptyState marginBottom={shouldShowDashboardEmptyState ? 16 : 0} />
                        ) : null}
                        {shouldShowDashboardEmptyState ? (
                            <DashboardsEmptyState
                                userWebsite={getUserWebsite()}
                                marginBottom={0}
                            />
                        ) : null}
                    </>
                )}
            </WidgetContainer>
        </WidgetWrapper>
    );
};
